import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import './AdminToolManual.css';

// ── Kinescope embed helpers ──────────────────────────────────────────────────

function toKinescopeEmbed(url) {
    if (!url) return '';
    if (url.includes('/embed/')) return url;
    const m = url.match(/kinescope\.ru\/([a-zA-Z0-9_-]+)/);
    return m ? `https://kinescope.ru/embed/${m[1]}` : url;
}

function VideoModal({ url, onClose }) {
    if (!url) return null;
    return (
        <div className="tmn-modal__overlay" onClick={onClose}>
            <div className="tmn-modal__box tmn-modal__box--video" onClick={e => e.stopPropagation()}>
                <button className="tmn-modal__close" onClick={onClose}>×</button>
                <div className="tmn-modal__video-wrap">
                    <iframe
                        src={toKinescopeEmbed(url)}
                        title="Видеоинструкция"
                        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                        allowFullScreen
                        frameBorder="0"
                    />
                </div>
            </div>
        </div>
    );
}

function TextModal({ title, text, onClose }) {
    return (
        <div className="tmn-modal__overlay" onClick={onClose}>
            <div className="tmn-modal__box" onClick={e => e.stopPropagation()}>
                <button className="tmn-modal__close" onClick={onClose}>×</button>
                <h3 className="tmn-modal__title">{title}</h3>
                <div className="tmn-modal__text">{text || 'Инструкция ещё не заполнена.'}</div>
            </div>
        </div>
    );
}

// ── Editable field ────────────────────────────────────────────────────────────

function EditableField({ value, onChange, placeholder, multiline, className = '' }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(value || '');
    useEffect(() => { setDraft(value || ''); }, [value]);
    const commit = () => { setEditing(false); if (draft !== value) onChange(draft); };
    if (editing) {
        const props = {
            autoFocus: true,
            value: draft,
            onChange: e => setDraft(e.target.value),
            onBlur: commit,
            onKeyDown: e => {
                if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
                if (!multiline && e.key === 'Enter') commit();
            },
        };
        return multiline
            ? <textarea {...props} className={`tmn__editable-area ${className}`} rows={4} />
            : <input {...props} className={`tmn__editable-input ${className}`} />;
    }
    return (
        <span
            className={`tmn__editable-text ${!value ? 'tmn__editable-text--empty' : ''} ${className}`}
            onClick={() => setEditing(true)}
            title="Нажми чтобы редактировать"
        >
            {value || placeholder || '—'}
        </span>
    );
}

// ── Tool row (Layer 3) ────────────────────────────────────────────────────────

function ToolRow({ tool, onUpdate, depth = 2 }) {
    const [preview, setPreview] = useState(null); // 'video' | 'text' | null
    const upd = (f, v) => onUpdate(tool.key, f, v);
    return (
        <tr className="tmn__tool-row" style={{ '--depth': depth }}>
            <td className="tmn__cell tmn__cell--icon">{tool.icon || '🔧'}</td>
            <td className="tmn__cell tmn__cell--title">
                {tool.title}
                <code className="tmn__tool-key">{tool.key}</code>
            </td>
            <td className="tmn__cell tmn__cell--video">
                <EditableField
                    value={tool.video_url}
                    onChange={v => upd('video_url', v)}
                    placeholder="Ссылка на kinescope.ru"
                />
                {tool.video_url && (
                    <button className="tmn__preview-btn" onClick={() => setPreview('video')}>▶ Видео</button>
                )}
            </td>
            <td className="tmn__cell tmn__cell--text">
                {tool.text_is_custom
                    ? <span className="tmn__origin tmn__origin--custom">переопределено вручную</span>
                    : tool.has_default_text && <span className="tmn__origin">эталон из кода</span>}
                <EditableField
                    value={tool.manual_text}
                    onChange={v => upd('text', v)}
                    placeholder="Текстовая инструкция…"
                    multiline
                />
                {tool.manual_text && (
                    <button className="tmn__preview-btn" onClick={() => setPreview('text')}>📄 Инструкция</button>
                )}
                {tool.text_is_custom && tool.has_default_text && (
                    <button
                        className="tmn__preview-btn tmn__reset-btn"
                        title="Очистить правку и вернуть эталонный текст из кода. Применится после «Сохранить»."
                        onClick={() => upd('text', '')}
                    >
                        ↺ Вернуть эталон
                    </button>
                )}
            </td>
            {preview === 'video' && <VideoModal url={tool.video_url} onClose={() => setPreview(null)} />}
            {preview === 'text' && <TextModal title={tool.title} text={tool.manual_text} onClose={() => setPreview(null)} />}
        </tr>
    );
}

// ── Module block (Layer 2) ────────────────────────────────────────────────────

function ModuleSection({ mod, onUpdateTool, collapsed, onToggle }) {
    return (
        <>
            <tr className="tmn__mod-row" onClick={onToggle} style={{ cursor: 'pointer' }}>
                <td className="tmn__cell tmn__cell--mod-label" colSpan={4}>
                    <span className="tmn__expand-icon">{collapsed ? '▶' : '▼'}</span>
                    {mod.label}
                    <code className="tmn__cat-key">({mod.key})</code>
                    <span className="tmn__cat-count">{mod.tools?.length || 0} инстр.</span>
                </td>
            </tr>
            {!collapsed && mod.tools?.map(tool => (
                <ToolRow key={tool.key} tool={tool} onUpdate={onUpdateTool} depth={3} />
            ))}
        </>
    );
}

// ── Section block (Layer 1) ───────────────────────────────────────────────────

function SectionBlock({ sec, onUpdateTool, collapsedMods, onToggleMod, collapsed, onToggle }) {
    const totalTools = (sec.modules || []).reduce((n, m) => n + (m.tools?.length || 0), 0)
        + (sec.direct_tools?.length || 0);
    return (
        <div className="tmn__section-block">
            <div className="tmn__sec-header" onClick={onToggle}>
                <span className="tmn__expand-icon">{collapsed ? '▶' : '▼'}</span>
                <span className="tmn__sec-icon">{sec.icon}</span>
                <span className="tmn__sec-label">{sec.label}</span>
                <code className="tmn__cat-key">({sec.key})</code>
                <span className="tmn__cat-count">{totalTools} инстр.</span>
            </div>
            {!collapsed && sec.key === 'basics' && (
                <div className="tmn__hint">
                    Статьи вводного раздела справки. Разметка текста: обычная строка — абзац,
                    <code>• пункт</code> — список, <code>1. пункт</code> — нумерованные шаги,
                    <code>| ячейка | ячейка</code> — строка таблицы (первая строка блока — заголовок),
                    <code>&gt; текст</code> — жёлтая плашка «важно», <code>[Заголовок] текст</code> — блок-пример.
                </div>
            )}
            {!collapsed && (
                <table className="tmn__table">
                    <thead>
                        <tr>
                            <th className="tmn__th-icon">Ик.</th>
                            <th>Название / ключ</th>
                            <th>Видео (kinescope.ru)</th>
                            <th>Текстовая инструкция</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sec.modules?.map(mod => (
                            <ModuleSection
                                key={mod.key}
                                mod={mod}
                                onUpdateTool={onUpdateTool}
                                collapsed={collapsedMods.has(mod.key)}
                                onToggle={() => onToggleMod(mod.key)}
                            />
                        ))}
                        {sec.direct_tools?.map(tool => (
                            <ToolRow key={tool.key} tool={tool} onUpdate={onUpdateTool} depth={2} />
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function AdminToolManual() {
    const { request } = useApi();
    const [sections, setSections]       = useState([]);
    const [systemTools, setSystemTools] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved]   = useState(false);
    const [error, setError]   = useState('');
    const [collapsedSecs, setCollapsedSecs] = useState(new Set());
    const [collapsedMods, setCollapsedMods] = useState(new Set());

    const load = useCallback(async () => {
        try {
            const res = await request('/api/v1/admin/tool-manual');
            setSections(JSON.parse(JSON.stringify(res.sections || [])));
            setSystemTools(JSON.parse(JSON.stringify(res.system_tools || [])));
        } catch (e) {
            setError('Ошибка загрузки: ' + (e.message || e));
        }
    }, [request]);

    useEffect(() => { load(); }, [load]);

    const updateTool = (key, field, val) => {
        const patch = t => t.key === key
            ? { ...t, [field === 'text' ? 'manual_text' : field]: val }
            : t;
        setSections(prev => prev.map(sec => ({
            ...sec,
            modules: sec.modules?.map(mod => ({ ...mod, tools: mod.tools?.map(patch) })),
            direct_tools: sec.direct_tools?.map(patch),
        })));
        setSystemTools(prev => prev.map(patch));
        setSaved(false);
    };

    const toggleSec = key => setCollapsedSecs(prev => {
        const s = new Set(prev);
        s.has(key) ? s.delete(key) : s.add(key);
        return s;
    });
    const toggleMod = key => setCollapsedMods(prev => {
        const s = new Set(prev);
        s.has(key) ? s.delete(key) : s.add(key);
        return s;
    });

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {};
            const collect = tools => tools?.forEach(t => {
                payload[t.key] = { video_url: t.video_url || '', text: t.manual_text || '' };
            });
            sections.forEach(sec => {
                sec.modules?.forEach(mod => collect(mod.tools));
                collect(sec.direct_tools);
            });
            collect(systemTools);

            const res = await request('/api/v1/admin/tool-manual', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            setSections(JSON.parse(JSON.stringify(res.sections || [])));
            setSystemTools(JSON.parse(JSON.stringify(res.system_tools || [])));
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            setError('Ошибка сохранения: ' + (e.message || e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tmn">
            <div className="tmn__header">
                <div>
                    <h2 className="tmn__title">Мануал инструментов</h2>
                    <p className="tmn__subtitle">
                        То же дерево Раздел → Модуль → Инструмент, что и в реестре. Для каждого инструмента —
                        ссылка на видеоинструкцию (kinescope.ru) и текстовая инструкция. Нажми на текст, чтобы редактировать.
                        Эталонные тексты лежат в коде и приезжают с деплоем; ручная правка перекрывает эталон.
                        Кнопка «↺ Вернуть эталон» (или просто пустое поле) снимает правку и возвращает текст из кода.
                    </p>
                </div>
                <div className="tmn__actions">
                    {saved && <span className="tmn__saved">Сохранено ✓</span>}
                    {error && <span className="tmn__error">{error}</span>}
                    <button className="tmn__save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? 'Сохраняю…' : 'Сохранить'}
                    </button>
                </div>
            </div>

            {sections.length === 0 && !error && <div className="tmn__loading">Загрузка…</div>}

            <div className="tmn__sections">
                {sections.map(sec => (
                    <SectionBlock
                        key={sec.key}
                        sec={sec}
                        onUpdateTool={updateTool}
                        collapsedMods={collapsedMods}
                        onToggleMod={toggleMod}
                        collapsed={collapsedSecs.has(sec.key)}
                        onToggle={() => toggleSec(sec.key)}
                    />
                ))}
            </div>

            {systemTools.length > 0 && (
                <div className="tmn__section-block tmn__section-block--system">
                    <div className="tmn__sec-header tmn__sec-header--system">
                        <span className="tmn__sec-icon">⚙️</span>
                        <span className="tmn__sec-label">Системные инструменты</span>
                        <span className="tmn__cat-count">{systemTools.length} инстр.</span>
                    </div>
                    <table className="tmn__table">
                        <thead>
                            <tr>
                                <th className="tmn__th-icon">Ик.</th>
                                <th>Название / ключ</th>
                                <th>Видео (kinescope.ru)</th>
                                <th>Текстовая инструкция</th>
                            </tr>
                        </thead>
                        <tbody>
                            {systemTools.map(tool => (
                                <ToolRow key={tool.key} tool={tool} onUpdate={updateTool} depth={1} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="tmn__footer">
                <button className="tmn__save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Сохраняю…' : 'Сохранить изменения'}
                </button>
                <a href="/admin/tool-registry" className="tmn__link">→ Реестр инструментов</a>
            </div>
        </div>
    );
}
